import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/services/firebase';
import { useAuth } from './useAuth';

type Period = 'day' | 'week' | 'month' | 'year' | 'all';

interface ConversionEvent {
  conversionId: string;
  clickId: string | null;
  userId: string;
  eventType: string;
  eventValue: number | null;
  eventCurrency: string | null;
  eventMetadata: string | null;
  pageUrl: string | null;
  timestamp: string;
  attributionMethod: string;
}

interface StatsResponse {
  totalClicks: number;
  clicksByDay: { date: string; clicks: number }[];
  clicksByCountry: { country: string; clicks: number }[];
  clicksByDevice: { device: string; clicks: number }[];
  clicksByBrowser: { browser: string; clicks: number }[];
  topLinks: { linkId: string; clicks: number }[];
  totalConversions: number;
  conversionRate: number;
  totalRevenue: number;
  avgOrderValue: number;
  conversionsByType: { type: string; count: number }[];
  conversionsByDay: { date: string; conversions: number }[];
  recentConversions: ConversionEvent[];
}

export const useStats = (period: Period = 'month', deeplinkId?: string) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const getStats = httpsCallable<
        { userId: string; period: Period; deeplinkId?: string },
        StatsResponse
      >(functions, 'getStats');

      const result = await getStats({
        userId: user.uid,
        period,
        deeplinkId,
      });

      setStats(result.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  }, [user, period, deeplinkId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};
